import { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Edit3, Trash2, Phone, Heart, X } from 'lucide-react';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import type { FamilyMember } from '../types';
import Spinner from '../../../components/ui/Spinner';
import { useToast } from '../../../components/ui/Toast';

const RELATIONS = ['SPOUSE', 'CHILD', 'PARENT', 'SIBLING', 'GRANDPARENT', 'OTHER'];

export default function FamilyProfiles() {
  const toast = useToast();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', relation: 'SPOUSE', dateOfBirth: '', gender: 'MALE', bloodGroup: '', phone: '', isEmergencyContact: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    patientApi.getFamily().then(setMembers)
      .catch((e: any) => toast.error(e.message || 'Could not load family members.'))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(load, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', relation: 'SPOUSE', dateOfBirth: '', gender: 'MALE', bloodGroup: '', phone: '', isEmergencyContact: false });
    setShowForm(true);
  };

  const openEdit = (m: FamilyMember) => {
    setEditId(m.id);
    setForm({
      name: m.name,
      relation: m.relation,
      dateOfBirth: m.dateOfBirth?.split('T')[0] || '',
      gender: m.gender || 'MALE',
      bloodGroup: m.bloodGroup || '',
      phone: m.phone || '',
      isEmergencyContact: m.isEmergencyContact || false,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editId) await patientApi.updateFamily(editId, form);
      else await patientApi.addFamily(form);
      setShowForm(false);
      toast.success(editId ? 'Family member updated' : 'Family member added');
      load();
    } catch (e: any) {
      toast.error(e.message || 'Could not save family member.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!confirm('Remove this family member?')) return;
    try {
      await patientApi.removeFamily(id);
      toast.success('Family member removed');
      load();
    } catch (e: any) {
      toast.error(e.message || 'Could not remove family member.');
    }
  };

  return (
    <div className="space-y-6" style={govPageContainer()}>
      <div className="flex items-center justify-between">
        <div>
          <h1 style={GOV_TYPE.pageTitle}>Family Members</h1>
          <p className="text-sm text-gray-500 mt-1">Manage family profiles linked to your account</p>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Member
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : members.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No family members added</p>
          <p className="text-sm text-gray-400 mt-1">Add family members to manage their health profiles</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {members.map(m => (
            <div key={m.id} className="card p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-pink-50 flex items-center justify-center shrink-0">
                    <span className="text-lg font-bold text-pink-600">{m.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                    <p className="text-xs text-gray-500">{m.relation}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(m)} className="p-1.5 text-gray-400 hover:text-teal-600 rounded"><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleRemove(m.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
                {m.bloodGroup && <span className="badge-gray">{m.bloodGroup}</span>}
                {m.gender && <span className="badge-gray">{m.gender}</span>}
                {m.phone && (
                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone}</span>
                )}
                {m.isEmergencyContact && (
                  <span className="badge-red flex items-center gap-1 text-[10px]"><Heart className="w-3 h-3" />Emergency Contact</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">{editId ? 'Edit' : 'Add'} Family Member</h2>
              <button onClick={() => setShowForm(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="label">Name *</label>
                <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Relation</label>
                  <select className="input" value={form.relation} onChange={e => setForm(f => ({ ...f, relation: e.target.value }))}>
                    {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Gender</label>
                  <select className="input" value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Date of Birth</label>
                  <input type="date" className="input" value={form.dateOfBirth} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Blood Group</label>
                  <input className="input" placeholder="e.g. O+" value={form.bloodGroup} onChange={e => setForm(f => ({ ...f, bloodGroup: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label">Phone</label>
                <input className="input" type="tel" inputMode="numeric" maxLength={10} placeholder="Phone number" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isEmergencyContact} onChange={e => setForm(f => ({ ...f, isEmergencyContact: e.target.checked }))} className="accent-teal-600" />
                <span className="text-sm text-gray-700">Emergency contact</span>
              </label>
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.name} className="btn-primary flex-1 flex items-center justify-center gap-2">
                {saving ? <Spinner size="sm" /> : editId ? 'Update' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
